import type { components } from "@sagot/api-client";

export type RegressionCase = components["schemas"]["RegressionCaseOut"];
export type RegressionRun = components["schemas"]["RegressionRunOut"];
export type RegressionCaseBody = components["schemas"]["RegressionCaseCreate"];

/** The error code the server answers when publishing is held back by the test cases (409). */
export const REGRESSION_NOT_PASSED = "regression_not_passed";
export const REGRESSION_RUNS = 3;
export const REGRESSION_POLL_MS = 800;
export const REGRESSION_ATTRIBUTE_KEY = /^[a-z][a-z0-9_]*$/;
const PENDING = new Set(["queued", "running"]);

/** What a member types for one case; each field is plain text so it can be checked before it is sent. */
export interface CaseForm {
  name: string;
  /** One visitor message per line; the first starts the flow, the next answer what it asks. */
  messages: string;
  tier: string;
  status: string;
  /** One `key=value` per line (for example `kyc_status=pending`). */
  attributes: string;
  path: string;
  handover: "any" | "yes" | "no";
  /** Words the flow's own replies must contain, one per line. */
  contains: string;
}

export const EMPTY_FORM: CaseForm = { name: "", messages: "", tier: "", status: "", attributes: "", path: "", handover: "any", contains: "" };

export type FormProblem = "name" | "messages" | "attributes" | "expected";
export type FormResult = { ok: true; body: RegressionCaseBody } | { ok: false; problem: FormProblem };

const lines = (text: string): string[] => text.split(/\r?\n/).map((line) => line.trim()).filter((line) => line.length > 0);
const asRecord = (value: unknown): Record<string, unknown> => (value !== null && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {});
const asText = (value: unknown): string => (typeof value === "string" ? value : "");
const asList = (value: unknown): string[] => (Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : []);

/** The form of a saved case (for editing). */
export function formOf(row: RegressionCase): CaseForm {
  const input = asRecord(row.input);
  const customer = asRecord(input.customer);
  const expected = asRecord(row.expected);
  const messages = Array.isArray(input.messages) ? input.messages.map((m) => asText(asRecord(m).text)) : [];
  const attributes = Object.entries(asRecord(customer.attributes)).map(([key, value]) => `${key}=${asText(value)}`);
  const handover = typeof expected.handover === "boolean" ? (expected.handover ? "yes" : "no") : "any";
  return {
    name: row.name, messages: messages.join("\n"), tier: asText(customer.tier), status: asText(customer.account_status),
    attributes: attributes.join("\n"), path: asText(expected.path_reached), handover, contains: asList(expected.answer_contains).join("\n"),
  };
}

function attributesOf(text: string): Record<string, string> | null {
  const found: Record<string, string> = {};
  for (const line of lines(text)) {
    const at = line.indexOf("=");
    const key = at > 0 ? line.slice(0, at).trim() : "";
    const value = at > 0 ? line.slice(at + 1).trim() : "";
    if (!REGRESSION_ATTRIBUTE_KEY.test(key) || value.length === 0) return null;
    found[key] = value;
  }
  return found;
}

/** The request body for a form, or the first thing wrong with it. */
export function bodyOf(form: CaseForm): FormResult {
  const name = form.name.trim();
  const messages = lines(form.messages);
  if (name.length === 0) return { ok: false, problem: "name" };
  if (messages.length === 0) return { ok: false, problem: "messages" };
  const attributes = attributesOf(form.attributes);
  if (attributes === null) return { ok: false, problem: "attributes" };
  const contains = lines(form.contains);
  const path = form.path.trim();
  if (path.length === 0 && form.handover === "any" && contains.length === 0) return { ok: false, problem: "expected" };
  const customer = { tier: form.tier.trim() || null, account_status: form.status.trim() || null, attributes };
  const expected: components["schemas"]["RegressionExpected"] = {};
  if (path.length > 0) expected.path_reached = path;
  if (form.handover !== "any") expected.handover = form.handover === "yes";
  if (contains.length > 0) expected.answer_contains = contains;
  return { ok: true, body: { name, input: { messages: messages.map((text) => ({ role: "visitor", text })), customer }, expected } };
}

export type RunView = "never" | "running" | "passed" | "failed" | "stale" | "error";

/** Where the last run of the cases stands: a pass that no longer covers the draft or the cases is "stale". */
export function runView(run: RegressionRun | null | undefined): RunView {
  if (run === null || run === undefined) return "never";
  if (PENDING.has(run.status)) return "running";
  if (run.status === "error") return "error";
  if (run.status === "failed") return "failed";
  return run.current ? "passed" : "stale";
}

export const isPending = (run: RegressionRun | null | undefined): boolean => run !== null && run !== undefined && PENDING.has(run.status);

/** Passed runs of one case, for example `3/3`. */
export const tally = (runs: readonly boolean[]): string => `${runs.filter(Boolean).length}/${runs.length}`;
